<template>
  <CustomModal
    :is-open="globalErrorModalStore.isOpen && isAllowedEnvironment"
    :show-backdrop="true"
    :backdrop-dismiss="true"
    @update:is-open="(value) => !value && globalErrorModalStore.closeError()"
    @dismiss="globalErrorModalStore.closeError()"
    :title="t('globalErrorModal.title')"
  >
    <div class="global-error-modal">
      <div class="global-error-modal__content">
        <IonIcon :icon="alertCircleOutline" class="global-error-modal__icon" />

        <h3>{{ t('globalErrorModal.subtitle') }}</h3>

        <!-- ── Carte 1 : Navigateur / Requête ── -->
        <div v-if="hasBrowserInfo" class="global-error-modal__card">
          <h4 class="global-error-modal__card-title">
            {{ t('globalErrorModal.browserCardTitle') }}
          </h4>

          <div v-if="details.browser.statusCode" class="global-error-modal__detail-row">
            <span class="global-error-modal__label">{{ t('globalErrorModal.statusCode') }}</span>
            <span class="global-error-modal__value">{{ details.browser.statusCode }}</span>
          </div>

          <div v-if="details.browser.errorType" class="global-error-modal__detail-row">
            <span class="global-error-modal__label">{{ t('globalErrorModal.errorType') }}</span>
            <span class="global-error-modal__value">{{ details.browser.errorType }}</span>
          </div>

          <div v-if="details.browser.timestamp" class="global-error-modal__detail-row">
            <span class="global-error-modal__label">{{ t('globalErrorModal.timestamp') }}</span>
            <span class="global-error-modal__value">{{ details.browser.timestamp }}</span>
          </div>

          <div
            v-if="details.browser.userAgent && !globalErrorModalStore.isStudent"
            class="global-error-modal__detail-row global-error-modal__detail-row--block"
          >
            <button
              type="button"
              class="global-error-modal__dropdown-toggle"
              @click="isUserAgentOpen = !isUserAgentOpen"
            >
              <span class="global-error-modal__label">User-Agent</span>
              <svg
                width="12"
                height="8"
                viewBox="0 0 12 8"
                fill="none"
                class="global-error-modal__chevron"
                :class="{ open: isUserAgentOpen }"
              >
                <path
                  d="M1 1.5L6 6.5L11 1.5"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
            <div
              v-if="isUserAgentOpen"
              class="global-error-modal__value global-error-modal__value--mono global-error-modal__value--scrollable"
            >
              {{ details.browser.userAgent }}
            </div>
          </div>

          <div
            v-if="details.browser.stack && !globalErrorModalStore.isStudent"
            class="global-error-modal__detail-row global-error-modal__detail-row--block"
          >
            <button
              type="button"
              class="global-error-modal__dropdown-toggle"
              @click="isStackOpen = !isStackOpen"
            >
              <span class="global-error-modal__label">Stack trace</span>
              <svg
                width="12"
                height="8"
                viewBox="0 0 12 8"
                fill="none"
                class="global-error-modal__chevron"
                :class="{ open: isStackOpen }"
              >
                <path
                  d="M1 1.5L6 6.5L11 1.5"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
            <div
              v-if="isStackOpen"
              class="global-error-modal__value global-error-modal__value--mono global-error-modal__value--scrollable"
            >
              {{ details.browser.stack }}
            </div>
          </div>
        </div>

        <!-- ── Carte 2 : Erreur interne (données API) ── -->
        <div
          v-if="hasInnerInfo && !globalErrorModalStore.isStudent"
          class="global-error-modal__card"
        >
          <h4 class="global-error-modal__card-title">
            {{ t('globalErrorModal.innerCardTitle') }}
          </h4>

          <div
            v-if="details.inner.message"
            class="global-error-modal__detail-row global-error-modal__detail-row--block"
          >
            <button
              type="button"
              class="global-error-modal__dropdown-toggle"
              @click="isInnerMessageOpen = !isInnerMessageOpen"
            >
              <span class="global-error-modal__label">Message</span>
              <svg
                width="12"
                height="8"
                viewBox="0 0 12 8"
                fill="none"
                class="global-error-modal__chevron"
                :class="{ open: isInnerMessageOpen }"
              >
                <path
                  d="M1 1.5L6 6.5L11 1.5"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
            <div
              v-if="isInnerMessageOpen"
              class="global-error-modal__value global-error-modal__value--mono global-error-modal__value--scrollable"
            >
              {{ details.inner.message }}
            </div>
          </div>

          <div
            v-if="details.inner.innerExceptionMessage"
            class="global-error-modal__detail-row global-error-modal__detail-row--block"
          >
            <button
              type="button"
              class="global-error-modal__dropdown-toggle"
              @click="isInnerExceptionOpen = !isInnerExceptionOpen"
            >
              <span class="global-error-modal__label">InnerExceptionMessage</span>
              <svg
                width="12"
                height="8"
                viewBox="0 0 12 8"
                fill="none"
                class="global-error-modal__chevron"
                :class="{ open: isInnerExceptionOpen }"
              >
                <path
                  d="M1 1.5L6 6.5L11 1.5"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
            <div
              v-if="isInnerExceptionOpen"
              class="global-error-modal__value global-error-modal__value--mono global-error-modal__value--scrollable"
            >
              {{ details.inner.innerExceptionMessage }}
            </div>
          </div>

          <div v-if="details.inner.code" class="global-error-modal__detail-row">
            <span class="global-error-modal__label">Code</span>
            <span class="global-error-modal__value">{{ details.inner.code }}</span>
          </div>

          <div
            v-if="details.inner.appData && Object.keys(details.inner.appData).length"
            class="global-error-modal__detail-row global-error-modal__detail-row--block"
          >
            <button
              type="button"
              class="global-error-modal__dropdown-toggle"
              @click="isAppDataOpen = !isAppDataOpen"
            >
              <span class="global-error-modal__label">{{ t('globalErrorModal.appData') }}</span>
              <svg
                width="12"
                height="8"
                viewBox="0 0 12 8"
                fill="none"
                class="global-error-modal__chevron"
                :class="{ open: isAppDataOpen }"
              >
                <path
                  d="M1 1.5L6 6.5L11 1.5"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
            <div
              v-if="isAppDataOpen"
              class="global-error-modal__value global-error-modal__value--mono global-error-modal__value--scrollable"
            >
              <div v-for="(value, key) in details.inner.appData" :key="String(key)">
                <strong>{{ key }}:</strong>
                {{ typeof value === 'object' ? JSON.stringify(value) : value }}
              </div>
            </div>
          </div>

          <div
            v-if="details.inner.rawBody"
            class="global-error-modal__detail-row global-error-modal__detail-row--block"
          >
            <button
              type="button"
              class="global-error-modal__dropdown-toggle"
              @click="isRawBodyOpen = !isRawBodyOpen"
            >
              <span class="global-error-modal__label">{{ t('globalErrorModal.rawBody') }}</span>
              <svg
                width="12"
                height="8"
                viewBox="0 0 12 8"
                fill="none"
                class="global-error-modal__chevron"
                :class="{ open: isRawBodyOpen }"
              >
                <path
                  d="M1 1.5L6 6.5L11 1.5"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
            <div
              v-if="isRawBodyOpen"
              class="global-error-modal__value global-error-modal__value--mono global-error-modal__value--scrollable"
            >
              {{ details.inner.rawBody }}
            </div>
          </div>
        </div>

        <!-- ── Carte 3 : Message personnel (contexte dev) ── -->
        <div v-if="hasPersonalInfo" class="global-error-modal__card">
          <h4 class="global-error-modal__card-title">
            {{ t('globalErrorModal.personalCardTitle') }}
          </h4>

          <div v-if="details.personal.context" class="global-error-modal__detail-row">
            <span class="global-error-modal__label">{{ t('globalErrorModal.context') }}</span>
            <span class="global-error-modal__value">{{ details.personal.context }}</span>
          </div>

          <div v-if="details.personal.requestType" class="global-error-modal__detail-row">
            <span class="global-error-modal__label">{{ t('globalErrorModal.requestType') }}</span>
            <span class="global-error-modal__value">{{ details.personal.requestType }}</span>
          </div>

          <div v-if="details.personal.resourceId" class="global-error-modal__detail-row">
            <span class="global-error-modal__label">{{ t('globalErrorModal.resourceId') }}</span>
            <span class="global-error-modal__value">{{ details.personal.resourceId }}</span>
          </div>

          <div
            v-if="details.personal.message"
            class="global-error-modal__detail-row global-error-modal__detail-row--block"
          >
            <span class="global-error-modal__label">Message</span>
            <span class="global-error-modal__value global-error-modal__value--mono">
              {{ details.personal.message }}
            </span>
          </div>
        </div>

        <p class="global-error-modal__help-text">
          {{ t('globalErrorModal.helpText') }}
        </p>

        <div class="global-error-modal__actions">
          <button class="global-error-modal__copy-btn" @click="copyErrorDetails">
            <IconRenderer
              :name="copied ? 'checkmark-outline' : 'copy-outline'"
              :size="'18'"
              :css-color="copied ? 'var(--ion-color-success)' : 'var(--ion-color-medium)'"
            />
            <span>{{ copied ? t('globalErrorModal.copied') : t('globalErrorModal.copy') }}</span>
          </button>
          <Button type="primary" @click="globalErrorModalStore.closeError()">
            {{ t('close') }}
          </Button>
        </div>
      </div>
    </div>
  </CustomModal>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { IonIcon } from '@ionic/vue';
import { alertCircleOutline } from 'ionicons/icons';
import { useI18n } from 'vue-i18n';
import CustomModal from '@/common/components/ui/modal/CustomModal.vue';
import Button from '@/common/components/ui/button/Button.vue';
import IconRenderer from '@/common/components/ui/icon/IconRenderer.vue';
import { useGlobalErrorModalStore } from '@/common/stores/globalErrorModalStore';

const props = withDefaults(
  defineProps<{
    devOnly?: boolean;
  }>(),
  { devOnly: false }
);

const { t } = useI18n();
const globalErrorModalStore = useGlobalErrorModalStore();
const copied = ref(false);
const isUserAgentOpen = ref(false);
const isStackOpen = ref(false);
const isInnerMessageOpen = ref(true);
const isInnerExceptionOpen = ref(true);
const isAppDataOpen = ref(false);
const isRawBodyOpen = ref(false);

const isAllowedEnvironment = computed(() => {
  if (!props.devOnly) return true;
  const mode = import.meta.env.VITE_APP_MODE;
  return mode === 'development' || mode === 'local';
});

const details = computed(() => globalErrorModalStore.details);

const hasBrowserInfo = computed(() => {
  const b = details.value.browser;
  return b && (b.statusCode || b.errorType || b.timestamp || b.userAgent || b.stack);
});

const hasInnerInfo = computed(() => {
  const i = details.value.inner;
  return (
    i &&
    (i.message ||
      i.innerExceptionMessage ||
      i.code ||
      (i.appData && Object.keys(i.appData).length > 0) ||
      i.rawBody)
  );
});

const hasPersonalInfo = computed(() => {
  const p = details.value.personal;
  return p && (p.context || p.requestType || p.resourceId || p.message);
});

const copyErrorDetails = async () => {
  const { browser, inner, personal } = details.value;
  const lines: string[] = [];

  lines.push('── Navigateur / Requête ──');
  if (browser.statusCode) lines.push(`${t('globalErrorModal.statusCode')}: ${browser.statusCode}`);
  if (browser.errorType) lines.push(`${t('globalErrorModal.errorType')}: ${browser.errorType}`);
  if (browser.timestamp) lines.push(`${t('globalErrorModal.timestamp')}: ${browser.timestamp}`);
  if (browser.userAgent) lines.push(`User-Agent: ${browser.userAgent}`);
  if (browser.stack) lines.push(`Stack: ${browser.stack}`);

  if (!globalErrorModalStore.isStudent) {
    lines.push('');
    lines.push('── Erreur interne ──');
    if (inner.message) lines.push(`Message: ${inner.message}`);
    if (inner.innerExceptionMessage)
      lines.push(`InnerExceptionMessage: ${inner.innerExceptionMessage}`);
    if (inner.code) lines.push(`Code: ${inner.code}`);
    if (inner.appData && Object.keys(inner.appData).length)
      lines.push(`AppData: ${JSON.stringify(inner.appData, null, 2)}`);
    if (inner.rawBody) lines.push(`Raw: ${inner.rawBody}`);
  }

  lines.push('');
  lines.push('── Message personnel ──');
  if (personal.context) lines.push(`${t('globalErrorModal.context')}: ${personal.context}`);
  if (personal.requestType)
    lines.push(`${t('globalErrorModal.requestType')}: ${personal.requestType}`);
  if (personal.resourceId)
    lines.push(`${t('globalErrorModal.resourceId')}: ${personal.resourceId}`);
  if (personal.message) lines.push(`Message: ${personal.message}`);

  await navigator.clipboard.writeText(lines.join('\n'));
  copied.value = true;
  setTimeout(() => (copied.value = false), 2000);
};
</script>

<style scoped>
.global-error-modal {
  background: var(--ion-card-background, #ffffff);
  border-radius: 12px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
}

.global-error-modal__content {
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
}

.global-error-modal__icon {
  font-size: 48px;
  color: var(--ion-color-danger, #eb445a);
}

.global-error-modal__content h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--ion-color-dark, #333);
}

.global-error-modal__details {
  width: 100%;
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.global-error-modal__card {
  width: 100%;
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  border-left: 3px solid var(--ion-color-primary, #3880ff);
}

.global-error-modal__card-title {
  margin: 0 0 4px;
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--ion-color-primary, #3880ff);
  text-align: left;
}

.global-error-modal__detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.global-error-modal__detail-row--block {
  flex-direction: column;
  align-items: flex-start;
}

.global-error-modal__label {
  font-weight: 600;
  color: var(--ion-color-dark, #333);
  font-size: 0.875rem;
}

.global-error-modal__value {
  color: var(--ion-color-medium, #666);
  font-size: 0.875rem;
  text-align: right;
}

.global-error-modal__value--mono {
  font-family: ui-monospace, 'Courier New', monospace;
  font-size: 0.8rem;
  text-align: left;
  word-break: break-all;
  background: #eef0f2;
  border-radius: 4px;
  padding: 6px 8px;
  width: 100%;
}

.global-error-modal__value--scrollable {
  min-height: 40px;
  max-height: 150px;
  overflow-y: auto;
  white-space: pre-wrap;
}

.global-error-modal__dropdown-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  gap: 8px;
}

.global-error-modal__chevron {
  transition: transform 0.2s;
  color: var(--ion-color-medium, #666);
}

.global-error-modal__chevron.open {
  transform: rotate(180deg);
}

.global-error-modal__help-text {
  margin: 0;
  color: var(--ion-color-medium, #666);
  line-height: 1.5;
  font-size: 0.875rem;
}

.global-error-modal__copy-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: 1px solid var(--ion-border-color-light, #e0e0e0);
  border-radius: 8px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 0.8rem;
  color: var(--ion-color-medium, #666);
  transition: all 0.2s ease;
}

.global-error-modal__copy-btn:hover {
  background: #f0f0f0;
  color: var(--ion-color-dark, #333);
}

.global-error-modal__actions {
  display: flex;
  gap: 12px;
  width: 100%;
  justify-content: center;
  align-items: center;
}

@media (max-width: 768px) {
  .global-error-modal {
    width: 95%;
    margin: 20px;
  }

  .global-error-modal__content {
    padding: 20px;
  }

  .global-error-modal__detail-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .global-error-modal__value {
    text-align: left;
  }

  .global-error-modal__actions {
    flex-direction: column;
  }
}
</style>
